import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import './UserGuide.css';

interface UserGuideProps {
  onClose: () => void;
  currentSection?: string;
}

const UserGuide: React.FC<UserGuideProps> = ({ onClose, currentSection }) => {
  const [activeSection, setActiveSection] = useState(currentSection || 'overview');

  const sections = [
    {
      id: 'overview',
      title: 'Getting Started',
      icon: '🚀',
      group: 'Getting Started',
      content: `# Welcome to Your Photography Website Management System

This interactive guide will help you master every aspect of your Carmen Lorraine Photography website. Whether you're uploading new galleries, writing blog posts, or customizing your site's appearance, this guide provides step-by-step instructions for every feature.

## Quick Start Checklist
- ✅ Access your admin panel at your website URL + /admin
- ✅ Familiarize yourself with the sidebar navigation
- ✅ Start with creating your first gallery
- ✅ Upload your logo and customize your content

## Navigation Tips
- Use the sidebar to switch between different sections
- Each section has its own help content
- Click the "?" icon in any section for specific help
- Use the search function to find specific topics

## New Features Added
- **Component Matrix**: Complete reference of all components and their purposes
- **Technical Glossary**: Definitions of terms like "slug", "API", "CRUD", etc.
- **Reorganized Structure**: Matches the new admin panel organization exactly`
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: '📊',
      group: 'Overview',
      content: `# Dashboard - Your Command Center

The Dashboard is your website's health monitor, showing you at a glance how your content is performing.

## What You'll See
- **Total Images**: Count of all images across galleries
- **Total Galleries**: Number of galleries created
- **Total Blog Posts**: Number of blog posts
- **Recent Activity**: Content created in the last 7 days

## How to Use
- View statistics at a glance
- Monitor recent activity
- No editing capabilities - purely informational
- Use as a starting point to navigate to other sections`
    },
    {
      id: 'galleries',
      title: 'Galleries',
      icon: '🖼️',
      group: 'Gallery Management',
      content: `# Galleries - Your Digital Portfolio

This is where the magic happens! Showcase your photography work to potential clients.

## Creating a Gallery
1. Click "Galleries" in the admin sidebar
2. Click "Add New Gallery" button
3. Fill in Gallery Details:
   - **Gallery Name**: Name of the gallery (e.g., "Wedding Portfolio")
   - **Category**: Select from existing categories
   - **Short Excerpt**: Brief preview text (1-2 sentences)
   - **Full Description**: Detailed description (optional)

## Adding Images
- **Upload Images**: Use the upload widget to add new images
- **Browse Cloudinary**: Select from existing images in your Cloudinary library
- **Drag & Drop**: Drag images directly into the upload area
- **Image Reordering**: Drag and drop to reorder images (first image is the hero)

## Image Management Features
- **Hero Image**: First image in the list becomes the hero image
- **Image Cropping**: Click on any image to open crop editor
- **Alt Text Editing**: Click on image, then "Edit Alt Text" for accessibility
- **Remove Images**: Click "Remove" button on individual images
- **Bulk Operations**: Select multiple images for bulk removal
- **Image Positioning**: Adjust hero image position and zoom`
    },
    {
      id: 'categories',
      title: 'Gallery categories',
      icon: '🏷️',
      group: 'Gallery Management',
      content: `# Categories - Organize Your Work

Create and manage categories to organize your galleries (e.g., "Weddings", "Portraits", "Commercial").

## Creating a Category
1. Click "Categories" in admin sidebar
2. Click "Add New Category" button
3. Fill in Category Details:
   - **Name**: Category name (e.g., "Weddings")
   - **Slug**: URL-friendly version (auto-generated) - *Creates clean web addresses like "wedding-photos"*
   - **Description**: Brief description
   - **Color**: Choose a color for category tags
   - **Order**: Display order (lower numbers appear first)

## Category Features
- **Color Coding**: Each category has a unique color
- **Automatic Slugs**: URL-friendly names generated automatically
- **Order Management**: Control display order
- **Gallery Association**: Galleries are linked to categories`
    },
    {
      id: 'search-photos',
      title: 'Photos for Google',
      icon: '🔍',
      group: 'Gallery Management',
      content: `# Photos for Google

Website > Also on the site > Photos for Google is where you choose the photographs Google receives with the homepage, and the full-frame picture used when someone shares a link. They are not shown as a row on the page.

## Homepage
1. Open **Photos for Google** under Website
2. Add 4 to 8 photographs from your galleries. Nothing is uploaded again
3. Write alt text for each one. Describe the subject and the Cape Town location in 5 to 15 words, for example "Family photo shoot on Noordhoek Beach, Cape Town"
4. Use Up and Down to set the order. The first photograph is the one Google is most likely to treat as the main picture
5. Choose **Share image** on the photograph that should appear when a link is shared. It is shown full-frame, not padded onto white
6. Click **Save**

## Gallery category pages
The Gallery category pages panel is optional. Each category, such as Family or Business and Commercial, can have up to four photographs and alt text for Google. They are not shown as a row on the page, because the galleries are already listed there. Leave a category empty and Google uses the first photograph from each gallery in the category.

## Until you save
If you have not saved a selection yet, the homepage uses the first photograph from each featured gallery, so Google still receives real photographs.`
    },
    {
      id: 'display-settings',
      title: 'Gallery layout',
      icon: '⚙️',
      group: 'Gallery Management',
      content: `# Display Settings - Gallery Presentation

Configure how your galleries appear on the homepage for the perfect visual experience.

## Gallery Layout Options
- **Mosaic Layout**: Varied sizes for visual interest
- **Grid Layout**: Uniform grid for clean organization
- **List Layout**: Simple list format

## Configuration Options
- **Images per row**: Control density
- **Show categories**: Display category filters
- **Animation effects**: Enable/disable transitions
- **Responsive settings**: Mobile, tablet, desktop layouts

## How to Configure
1. Click "Gallery layout" under Galleries
2. Choose your preferred layout
3. Adjust additional options
4. Click "Save Settings" to apply changes`
    },
    {
      id: 'blogs',
      title: 'Blog',
      icon: '📝',
      group: 'Blog Management',
      content: `# Blog Posts - Share Your Story

Create and manage blog posts with different layouts and advanced features.

## Blog Types Available
- **Standard Blog**: Regular blog posts with text and images
- **Portfolio**: Showcase client work with detailed information
- **Story**: Personal storytelling with mood and quotes
- **Magazine**: Multi-section articles with featured content

## Creating a Blog Post
1. Click "Blog Posts" in admin sidebar
2. Click "Add New Blog Post" button
3. Choose Blog Type: Standard, Portfolio, Story, or Magazine
4. Fill in Basic Details:
   - **Title**: Blog post title
   - **Slug**: URL-friendly version (auto-generated) - *Creates clean web addresses like "my-blog-post"*
   - **Excerpt**: Short description for previews
   - **Author**: Your name (defaults to "Carmen Lorraine")
   - **Content**: Main blog content (rich text editor)

## Publishing Options
- **Published**: Checkbox to make post visible on homepage
- **Featured**: Checkbox to highlight this post
- **Categories**: Select from existing blog categories

## Advanced Features
- **Image Management**: Drag & drop, multi-select, alt text editing
- **SEO Settings**: Meta titles, descriptions, keywords
- **Type-Specific Fields**: Different fields based on blog type (Portfolio, Story, Magazine)`
    },
    {
      id: 'blog-categories',
      title: 'Blog Categories',
      icon: '📁',
      group: 'Blog Management',
      content: `# Blog Categories

The list shows each blog section. **Add category** creates a new one. **Edit** opens that section so you can change its title, heading, introduction, and Google listing.

## What you can change
- **Title**: the name in the menu and on each story
- **Page heading**: the large heading on the category page
- **Introduction**: the sentence under that heading
- **Google title** and **meta description**: the listing in search results

## Address
A new category gets its web address from the title, and you can adjust it before the first save. After that, the address stays the same so existing links keep working.

The new section appears in the blog menu and in the category list when you edit a post. A category can be removed only when no story uses it.`
    },
    {
      id: 'packages',
      title: 'Packages',
      icon: '🎁',
      group: 'Services',
      content: `# Packages - Your Photography Services

Manage your photography packages and pricing to showcase your services to potential clients.

## Creating a Package
1. Click "Packages" in admin sidebar
2. Click "Add New Package" button
3. Fill in Package Details:
   - **Package Name**: Name of the service (e.g., "Wedding Photography")
   - **Category**: Service category
   - **Short Description**: Brief overview
   - **Full Description**: Detailed package information
   - **Price**: Base pricing
   - **Currency**: R or USD
   - **Duration**: Session length in minutes
   - **Features**: List of included features (one per line)

## Package Options
- **Badge**: Popular, New, Special, or Limited
- **Featured**: Highlight this package
- **Available**: Make package bookable
- **Display Order**: Control display sequence
- **Special Pricing**: Set special offers with start/end dates

## Package Features
- **Price Tiers**: Multiple pricing options
- **Add-ons**: Additional services
- **Image Galleries**: Showcase your work
- **Related Content**: Link to galleries and blog posts
- **Customization**: Process steps, expectations, testimonials

## Advanced Package Customization
### Modal Customization
- **Show Process Steps**: Display "How It Works" section
- **Show Expectations**: Display "What to Expect" section
- **Show Testimonials**: Display "What Clients Say" section
- **Custom Booking Note**: Override default booking message
- **Select Elements**: Choose which process steps, expectations, and testimonials to show

### Image Management
- **Upload Images**: Use Cloudinary upload widget
- **Set Hero Image**: First image becomes main display
- **Manage Gallery**: Add multiple images, reorder, or remove
- **Image Actions**: Set as hero, replace, or remove individual images`
    },
    {
      id: 'package-customization',
      title: 'Package text',
      icon: '⚙️',
      group: 'Services',
      content: `# Package Customization - Global Elements

Configure global elements that appear in all package modals, including process steps, client expectations, and testimonials.

## Global Booking Note
Set the default message shown in all package modals. This can be overridden per package.

## Process Steps Configuration
### How to Add Process Steps
1. Click "Add Step" button
2. **Step Number**: Automatically assigned
3. **Step Title**: e.g., "Initial Consultation"
4. **Step Description**: Detailed explanation
5. Click "Save Customization"

### Process Step Examples
- **Step 1**: Initial Consultation - Discuss vision and requirements
- **Step 2**: Planning & Preparation - Wardrobe and location guidance
- **Step 3**: Photography Session - Professional photography with guidance
- **Step 4**: Editing & Delivery - Professional editing and gallery delivery

## What to Expect Configuration
### How to Add Expectations
1. Click "Add Expectation" button
2. **Icon**: FontAwesome icon class (e.g., "fas fa-camera")
3. **Title**: e.g., "Professional Equipment"
4. **Description**: What clients can expect
5. Click "Save Customization"

### Expectation Examples
- **Professional Equipment**: State-of-the-art camera gear
- **Personal Touch**: Tailored to your unique style
- **Flexible Timing**: Work around your schedule
- **Quality Guarantee**: Professional editing and retouching

## Testimonials Configuration
### How to Add Testimonials
1. Click "Add Testimonial" button
2. **Content**: Client review text
3. **Author**: Client name
4. **Service**: Service type (e.g., "Wedding Package")
5. Click "Save Customization"

### Testimonial Examples
- **Content**: "Carmen has an incredible eye for detail..."
- **Author**: "Sarah M."
- **Service**: "Portrait Package"

## Customization Features
- **Global Settings**: Apply to all packages by default
- **Per-Package Override**: Individual packages can customize selections
- **Easy Management**: Add, edit, or remove elements easily
- **Flexible Selection**: Choose which elements appear in each package`
    },
    {
      id: 'content',
      title: 'Homepage text',
      icon: '✏️',
      group: 'Brand & Customization',
      content: `# Website Content - Your Homepage Text

Edit the main content that appears on your homepage.

## Editable Content
### Business Information
- **Studio Name**: Your photography business name
- **Hero Description**: Main description that appears on your homepage
- **About Description**: Detailed about section for your About page

### Contact Information
- **Contact Email**: Your email address for inquiries
- **Contact Phone**: Your phone number
- **Location**: Your location (e.g., "Cape Town, South Africa")

## How to Edit Content
1. Click "Homepage text" under Also on the site
2. Edit any of the text fields
3. Click "Save Content"
4. Changes appear immediately on homepage

## Content Features
- **Real-time Updates**: Changes appear instantly
- **Rich Text**: Basic formatting available
- **Contact Info**: Centralized contact management
- **SEO Friendly**: Optimized for search engines`
    },
    {
      id: 'logo',
      title: 'Logo',
      icon: '🖼️',
      group: 'Brand & Customization',
      content: `# Logo Management - Your Brand Identity

Upload and manage your business logo.

## How to Upload a Logo
1. Click "Logo" under Look
2. Click "Upload New Logo" button
3. Choose Image:
   - **File Types**: JPG, PNG, SVG supported
   - **Recommended Size**: 200x200px or larger
   - **Format**: Square aspect ratio works best
4. Set Alt Text for accessibility
5. Click "Upload Logo"
6. Logo appears immediately on website

## Logo Features
- **Cloudinary Integration**: Secure cloud storage
- **Alt Text Support**: Accessibility-friendly
- **Dimension Display**: Shows image size
- **Instant Updates**: Changes appear immediately
- **Responsive Design**: Scales on all devices`
    },
    {
      id: 'seo',
      title: 'SEO Dashboard',
      icon: '🔍',
      group: 'SEO & Marketing',
      content: `# SEO Dashboard - Search Engine Optimization

The SEO Dashboard is your comprehensive tool for optimizing your website's visibility in search engines. Every page, gallery, blog post, and service can be individually optimized for maximum search engine performance.

## Overview Tab

### SEO Score
Your overall SEO health score (0-100) based on:
- Content quality and completeness
- Technical SEO elements
- Image optimization
- Meta tag coverage

### Key Statistics
- **Total Pages**: All pages on your website
- **Total Images**: All images across galleries
- **Blog Posts**: Number of published blog posts
- **Missing Alt Tags**: Images without descriptive alt text

### Quick Actions
- **Check SEO Issues**: Run a comprehensive SEO audit
- **Generate Sitemap**: Create/update your sitemap.xml
- **Fix Common Issues**: One-click fixes for common problems

## Pages Tab

### Visual Page Management
1. Click "Pages" tab in SEO Dashboard
2. See all pages in a visual grid with status indicators:
   - ✅ **Green (Complete)**: All SEO elements configured
   - ⚠️ **Yellow (Partial)**: Some elements missing
   - ❌ **Red (Incomplete)**: Critical elements missing
3. Click any page card to view/edit its SEO settings
4. See live preview of how page appears in search results

### Page SEO Settings
For each page, you can configure:
- **Title**: Page title (50-60 characters optimal)
- **Description**: Meta description (150-160 characters optimal)
- **Keywords**: Relevant keywords for the page
- **Social Image**: Image for social media sharing (1200x630px recommended)
- **Canonical URL**: Prevent duplicate content issues

## Content Tab

### Bulk SEO Operations
Perform bulk operations across all content:
- **Generate Alt Text**: Auto-generate alt text for all images
- **Generate Meta Descriptions**: Create descriptions from content
- **Optimize Images**: Batch optimize for social sharing
- **Apply SEO Template**: Apply templates to multiple pages
- **Fix Common Issues**: Automatically fix detected issues
- **Validate URLs**: Check all canonical URLs

### Individual Content SEO
- **Gallery SEO**: Edit SEO for each gallery individually
- **Blog Post SEO**: Configure SEO for each blog post
- **Service SEO**: Optimize each service/package page

## Sitemap Tab

### Generate Sitemap
1. Click "Sitemap" tab
2. View all URLs that will be included
3. Click "Generate Sitemap" to create/update
4. Download the XML file
5. Upload to your website root directory
6. Submit to Google Search Console

### Sitemap Features
- Automatically includes all pages, galleries, blog posts, and services
- Updates lastmod dates from actual content
- Sets proper priorities and change frequencies
- Can be downloaded or copied to clipboard

## Health Check Tab

### Run SEO Audit
1. Click "Health Check" tab
2. Click "Run Check" button
3. Review issues grouped by severity:
   - **Critical**: Must fix immediately
   - **Warning**: Should fix soon
   - **Info**: Recommendations for improvement

### Issue Detection
The health checker finds:
- Missing meta tags (title, description, image)
- Missing structured data
- Duplicate content warnings
- Broken canonical URLs
- Missing alt text on images
- Missing gallery/blog descriptions

### One-Click Fixes
Many issues can be fixed automatically:
- Click "Fix" button on any fixable issue
- Changes are applied immediately
- Refresh to see updated status

## Settings Tab

### Global SEO Defaults
Set default SEO settings that apply site-wide:
1. Click "Settings" tab
2. Configure default title, description, keywords
3. Set default social sharing image
4. Configure default author
5. Click "Save" to apply

### Per-Page Overrides
Override defaults for specific pages:
1. Use visual page selector to choose a page
2. Or enter custom path (e.g., "/blog/my-post")
3. Enable override for that page
4. Configure page-specific SEO settings
5. Settings override defaults for that page only

### SEO Presets
Quick apply pre-configured SEO templates:
- **Portraits Preset**: Optimized for portrait photography
- **Weddings Preset**: Optimized for wedding photography
- **Reset to Defaults**: Restore original settings

## Best Practices

### Title Tags
- Keep between 50-60 characters
- Include your brand name
- Make it descriptive and compelling
- Include primary keyword

### Meta Descriptions
- Keep between 150-160 characters
- Write compelling copy that encourages clicks
- Include call-to-action
- Include relevant keywords naturally

### Images
- Use 1200x630px for social sharing images
- Always include descriptive alt text
- Optimize file sizes for fast loading
- Use relevant, high-quality images

### Keywords
- Use 5-10 relevant keywords per page
- Include location-based keywords (e.g., "Cape Town")
- Use long-tail keywords for better targeting
- Avoid keyword stuffing

## SEO Preview Cards

Every SEO editor includes live previews showing:
- **Google Search Result**: How your page appears in Google
- **Facebook Share Preview**: How it looks when shared on Facebook
- **Twitter Card Preview**: How it appears on Twitter

Use these previews to optimize your titles and descriptions for maximum impact.`
    },
    {
      id: 'seo-pages',
      title: 'Page SEO Management',
      icon: '📄',
      group: 'SEO & Marketing',
      content: `# Page SEO Management - Optimize Every Page

Manage SEO settings for all your website pages individually with visual tools and live previews.

## Accessing Page SEO

1. Go to **SEO Dashboard** in admin sidebar
2. Click **"Pages"** tab
3. See all pages in a visual grid layout

## Visual Page Selector

### Page Status Indicators
Each page card shows its SEO status:
- ✅ **Green Badge (Complete)**: Title, description, and image all configured
- ⚠️ **Yellow Badge (Partial)**: Some elements present but incomplete
- ❌ **Red Badge (Incomplete)**: Missing critical SEO elements

### Page Information Display
Each card shows:
- **Page Name**: Home, About, Contact, etc.
- **Current Title**: Preview of configured title
- **Current Description**: Preview of meta description
- **Status Badge**: Visual indicator of completeness

## Editing Page SEO

### Step-by-Step Guide

1. **Select a Page**
   - Click on any page card in the grid
   - Or use the page selector in Settings tab

2. **View Current Settings**
   - See what's currently configured
   - Preview how it appears in search results

3. **Edit SEO Elements**
   - **Title**: Enter compelling page title (50-60 chars)
   - **Description**: Write meta description (150-160 chars)
   - **Keywords**: Add relevant keywords (comma-separated)
   - **Social Image**: Enter URL for social sharing image

4. **Preview Results**
   - See Google search result preview
   - See Facebook share preview
   - See Twitter card preview
   - Character counts show if you're in optimal range

5. **Save Settings**
   - Click "Save" button
   - Settings apply immediately
   - Page status updates in the grid

## Page-Specific SEO Tips

### Homepage (/)
- Focus on your main value proposition
- Include location (e.g., "Cape Town")
- Include primary service types
- Use your brand name

### About Page (/about)
- Emphasize your experience and style
- Include location and service area
- Mention specialties and unique selling points
- Keep it personal and authentic

### Contact Page (/contact)
- Include location keywords
- Mention response time or availability
- Include service area
- Make it easy to find contact info

### Galleries Page (/galleries)
- Emphasize portfolio and work quality
- Include photography types offered
- Mention location if relevant
- Use action words (e.g., "Explore", "View")

### Blog Page (/blog)
- Focus on content value
- Mention topics covered
- Include "blog" or "stories" keywords
- Emphasize regular updates

### Services Page (/services)
- List main service types
- Include pricing keywords if appropriate
- Mention location
- Use compelling action words

## Using SEO Preview Cards

### Google Search Preview
- Shows exactly how your page appears in Google
- Title character count (optimal: 50-60)
- Description character count (optimal: 150-160)
- URL display

### Facebook Preview
- Shows how link appears when shared on Facebook
- Image preview (1200x630px recommended)
- Title and description display
- Helps optimize for social engagement

### Twitter Preview
- Shows Twitter Card appearance
- Large image card format
- Title and description
- Optimize for Twitter sharing

## Quick Tips

- **Update Regularly**: Keep SEO settings current with your content
- **Test Sharing**: Share your pages on social media to see how they appear
- **Monitor Performance**: Use Google Search Console to track performance
- **Be Consistent**: Use consistent branding and messaging across all pages`
    },
    {
      id: 'seo-content',
      title: 'Content SEO Management',
      icon: '📝',
      group: 'SEO & Marketing',
      content: `# Content SEO Management - Optimize Galleries, Blogs & Services

Each piece of content (gallery, blog post, service) can have its own SEO settings for maximum visibility.

## Gallery SEO

### Accessing Gallery SEO
1. Go to **Galleries** in admin sidebar
2. Click **"Edit"** on any gallery
3. Look for **"SEO"** tab or section in the edit modal

### Gallery SEO Settings
- **Title**: Gallery name with keywords (e.g., "Wedding Photography Portfolio - Cape Town")
- **Description**: Compelling description of the gallery content
- **Keywords**: Relevant keywords (e.g., "wedding photography, Cape Town, portraits")
- **Social Image**: Hero image URL optimized for sharing (1200x630px)

### Gallery SEO Best Practices
- Include photography type in title
- Mention location if relevant
- Describe the style or mood
- Use keywords naturally in description
- Choose best image for social sharing

## Blog Post SEO

### Accessing Blog Post SEO
1. Go to **Blog Posts** in admin sidebar
2. Click **"Create"** or **"Edit"** on a post
3. Look for **"SEO"** tab in the blog editor

### Blog Post SEO Settings
- **Title**: Blog post title (can override default)
- **Meta Description**: Compelling excerpt for search results
- **Keywords**: Tags and categories as keywords
- **Social Image**: Hero image for social sharing

### Blog Post SEO Best Practices
- Use the post title as SEO title (unless you need to optimize)
- Write compelling meta description that encourages clicks
- Include all relevant tags as keywords
- Use the hero image for social sharing
- Include location if post is location-specific

## Service/Package SEO

### Accessing Service SEO
1. Go to **Packages** in admin sidebar
2. Click **"Edit"** on any service
3. Look for **"SEO"** tab in the service editor

### Service SEO Settings
- **Title**: Service name with keywords
- **Description**: Service description optimized for search
- **Keywords**: Service category and related terms
- **Social Image**: Service image for sharing

### Service SEO Best Practices
- Include service type in title
- Mention pricing range if appropriate
- Include location keywords
- Use action words (e.g., "Book", "Hire", "Get")
- Highlight unique features

## Bulk SEO Operations

### Generate Alt Text
1. Go to **SEO Dashboard** → **Content** tab
2. Click **"Generate Alt Text"** card
3. System auto-generates alt text from filenames
4. Review and edit as needed

### Generate Meta Descriptions
1. Click **"Generate Meta Descriptions"** card
2. System creates descriptions from content
3. Review and customize for each item
4. Save changes

### Optimize Images
1. Click **"Optimize Images"** card
2. System optimizes all images for social sharing
3. Creates 1200x630px versions
4. Updates image URLs automatically

### Apply SEO Template
1. Click **"Apply SEO Template"** card
2. Select template (Portrait, Wedding, etc.)
3. Choose which content to apply to
4. Template settings applied automatically

## Individual Content Editors

### Gallery SEO Editor
- Access from Gallery Edit Modal
- Live preview of search result appearance
- Character count indicators
- Save to gallery-specific SEO settings

### Blog SEO Editor
- Access from Blog Create/Edit Modal
- Article-specific SEO options
- Publication date and author info
- Category and tag integration

### Service SEO Editor
- Access from Service Management
- Product/service schema support
- Pricing information in structured data
- Availability and offer details

## SEO Preview for Content

Each content editor includes:
- **Google Preview**: How it appears in search
- **Facebook Preview**: Social sharing appearance
- **Twitter Preview**: Twitter card format
- **Character Counts**: Real-time feedback on length

Use these previews to optimize your content for maximum visibility and engagement.`
    },
    {
      id: 'seo-health',
      title: 'SEO Health Check',
      icon: '🏥',
      group: 'SEO & Marketing',
      content: `# SEO Health Check - Comprehensive SEO Audit

The Health Check tool scans your entire website for SEO issues and provides actionable recommendations.

## Running a Health Check

### Step-by-Step Process

1. **Access Health Check**
   - Go to **SEO Dashboard** in admin sidebar
   - Click **"Health Check"** tab

2. **Run the Check**
   - Click **"Run Check"** button
   - System scans all pages and content
   - Results appear in seconds

3. **Review Results**
   - Issues grouped by severity
   - See which pages have problems
   - Understand what needs fixing

## Issue Severity Levels

### Critical Issues (Red)
Must fix immediately:
- Missing page titles
- Missing meta descriptions
- Broken canonical URLs
- Critical structured data missing

### Warnings (Yellow)
Should fix soon:
- Missing alt text on images
- Missing gallery descriptions
- Missing blog excerpts
- Suboptimal title/description lengths

### Info (Blue)
Recommendations:
- Duplicate content warnings
- Optimization suggestions
- Best practice recommendations

## Common Issues Found

### Missing Alt Text
- **Problem**: Images without descriptive alt text
- **Impact**: Poor accessibility and SEO
- **Fix**: Click "Fix" to auto-generate from filenames
- **Manual Fix**: Edit images individually in gallery editor

### Missing Descriptions
- **Problem**: Galleries or pages without descriptions
- **Impact**: Lower search visibility
- **Fix**: Add descriptions in content editor
- **Bulk Fix**: Use "Generate Meta Descriptions" tool

### Missing Meta Tags
- **Problem**: Pages without proper SEO meta tags
- **Impact**: Poor search engine visibility
- **Fix**: Configure in Page SEO Management
- **Quick Fix**: Use SEO presets

### Duplicate Content
- **Problem**: Multiple pages with same title/description
- **Impact**: Search engines may penalize
- **Fix**: Make each page unique
- **Prevention**: Use page-specific SEO settings

## One-Click Fixes

Many issues can be fixed automatically:

1. **Click "Fix" Button**
   - Available on fixable issues
   - Shows what will be changed
   - Confirms before applying

2. **Review Changes**
   - See what was fixed
   - Verify changes are correct
   - Edit manually if needed

3. **Re-run Check**
   - Verify issues are resolved
   - Check for new issues
   - Monitor improvement

## Health Check Features

### Real-Time Validation
- Checks all pages instantly
- Validates meta tags
- Verifies structured data
- Tests canonical URLs

### Issue Grouping
- Grouped by severity
- Grouped by page
- Grouped by issue type
- Easy to prioritize fixes

### Progress Tracking
- See improvement over time
- Track fixed issues
- Monitor SEO score changes
- Set goals for improvement

## Best Practices

### Regular Checks
- Run health check weekly
- Fix issues as they appear
- Monitor SEO score trends
- Keep content optimized

### Prioritize Fixes
1. Fix critical issues first
2. Address warnings next
3. Review info recommendations
4. Maintain good SEO health

### Monitor Progress
- Track SEO score over time
- Note which fixes help most
- Focus on high-impact changes
- Maintain consistent quality`
    },
    {
      id: 'seo-sitemap',
      title: 'Sitemap Management',
      icon: '🗺️',
      group: 'SEO & Marketing',
      content: `# Sitemap Management - Help Search Engines Find Your Content

A sitemap tells search engines about all the pages on your website, helping them index your content more effectively.

## What is a Sitemap?

A sitemap is an XML file that lists all URLs on your website with:
- **Location (URL)**: Where the page is located
- **Last Modified**: When the page was last updated
- **Change Frequency**: How often the page changes
- **Priority**: Relative importance of the page

## Generating Your Sitemap

### Step-by-Step Guide

1. **Access Sitemap Generator**
   - Go to **SEO Dashboard** in admin sidebar
   - Click **"Sitemap"** tab

2. **View Current Sitemap**
   - See all URLs that will be included
   - Check last modified dates
   - Verify priorities are correct

3. **Generate Sitemap**
   - Click **"Regenerate Sitemap"** button
   - System includes:
     - All main pages (Home, About, Contact, etc.)
     - All galleries
     - All blog posts
     - All services/packages

4. **Download Sitemap**
   - Click **"Download XML"** button
   - Save sitemap.xml file
   - Upload to your website root directory

5. **Submit to Search Engines**
   - Submit to Google Search Console
   - Submit to Bing Webmaster Tools
   - Add to robots.txt (already done automatically)

## Sitemap Contents

### Main Pages
- Homepage (/)
- Galleries (/galleries)
- Blog (/blog)
- Services (/services)
- About (/about)
- Contact (/contact)

### Dynamic Content
- Individual galleries (/gallery/:id)
- Blog posts (/blog/:slug)
- Service pages (/services/:slug)

### Automatic Updates
- Last modified dates from actual content
- Priorities based on page importance
- Change frequencies based on content type
- Filters out unpublished/unavailable content

## Sitemap Priorities

Pages are assigned priorities (0.0 to 1.0):
- **1.0**: Homepage (most important)
- **0.9**: Main category pages (Galleries, Services)
- **0.8**: Individual galleries
- **0.7**: Service detail pages, About, Contact
- **0.6**: Blog posts

## Change Frequencies

How often pages are expected to change:
- **Weekly**: Homepage, main category pages
- **Monthly**: Individual galleries, services, blog posts
- **Never**: Static pages like About (unless updated)

## Submitting Your Sitemap

### Google Search Console
1. Go to Google Search Console
2. Navigate to Sitemaps section
3. Enter: https://yourdomain.com/sitemap.xml
4. Click "Submit"
5. Google will crawl your sitemap

### Bing Webmaster Tools
1. Go to Bing Webmaster Tools
2. Navigate to Sitemaps
3. Submit your sitemap URL
4. Bing will index your pages

### Automatic Submission
Your sitemap is automatically referenced in robots.txt, so search engines can find it automatically.

## Sitemap Best Practices

### Keep It Updated
- Regenerate sitemap when adding new content
- Update after major content changes
- Regular updates help search engines stay current

### Verify Contents
- Check that all important pages are included
- Verify URLs are correct
- Ensure no broken links

### Monitor Performance
- Check Google Search Console for sitemap status
- Fix any errors reported
- Monitor indexing progress`
    },
    {
      id: 'seo-settings',
      title: 'SEO Settings',
      icon: '⚙️',
      group: 'SEO & Marketing',
      content: `# SEO Settings - Global Configuration

Configure default SEO settings that apply across your entire website, with the ability to override for specific pages.

## Global SEO Defaults

### Setting Defaults

1. **Access Settings**
   - Go to **SEO Dashboard** in admin sidebar
   - Click **"Settings"** tab

2. **Configure Defaults**
   - **Default Title**: Site-wide title template
   - **Default Description**: Default meta description
   - **Default Author**: Your name or brand
   - **Default Keywords**: Site-wide keywords
   - **Default Social Image**: Default sharing image
   - **Canonical URL**: Base URL for canonical tags

3. **Save Settings**
   - Click "Save" to apply defaults
   - Defaults apply to all pages unless overridden

## Visual Page Selector

### Using the Page Selector

1. **See All Pages**
   - Visual grid shows all main pages
   - Icons make pages easy to identify
   - Status indicators show which have overrides

2. **Select a Page**
   - Click any page button to select it
   - Page path auto-fills in the form
   - If page has override, settings load automatically

3. **Enable Override**
   - Check "Enable override for this page"
   - Page-specific settings take precedence
   - Override only affects selected page

## Per-Page Overrides

### Creating Page-Specific SEO

1. **Select Page**
   - Use visual page selector
   - Or enter custom path manually

2. **Enable Override**
   - Check "Enable override" checkbox
   - Form fields become editable

3. **Configure Settings**
   - **Page Title**: Override default title
   - **Page Description**: Override default description
   - **Page Keywords**: Page-specific keywords
   - **Page Social Image**: Page-specific sharing image
   - **Noindex**: Prevent indexing (use carefully)

4. **Copy Defaults**
   - Click "Copy Defaults Into Page" to start with defaults
   - Then customize for that specific page
   - Saves time on similar pages

5. **Save Override**
   - Settings saved automatically
   - Override applies only to that page
   - Other pages still use defaults

## SEO Presets

### Quick Apply Templates

**Portraits Preset**
- Optimized for portrait photography
- Includes portrait-related keywords
- Cape Town location focus
- Professional portrait emphasis

**Weddings Preset**
- Optimized for wedding photography
- Wedding-specific keywords
- Event photography focus
- Timeless storytelling emphasis

**Reset to Defaults**
- Restores original default settings
- Removes all customizations
- Use if you want to start fresh

## Settings Features

### Default Title Format
Recommended format: "Page Title | Carmen Lorraine Photography"
- Brand name included automatically
- Consistent across all pages
- Professional appearance

### Default Description
Should be:
- 150-160 characters
- Compelling and descriptive
- Include primary keywords
- Encourage clicks

### Default Keywords
Include:
- Your main service types
- Location keywords
- Brand name
- Industry terms

### Default Social Image
- Use your logo or best work image
- 1200x630px recommended
- High quality
- Represents your brand

## Advanced Settings

### Canonical URLs
- Prevents duplicate content issues
- Set base URL for your site
- Automatically applied to all pages
- Can override per-page if needed

### Noindex Settings
- **Global Noindex**: Hide entire site from search engines (use for staging)
- **Page Noindex**: Hide specific pages (use for private content)
- **Warning**: Only use if you don't want pages indexed

## Best Practices

### Start with Defaults
1. Set good global defaults first
2. Most pages will use these
3. Only override when necessary
4. Keeps settings consistent

### Use Presets
- Apply preset that matches your focus
- Customize from there
- Saves time on initial setup
- Ensures best practices

### Regular Reviews
- Review defaults quarterly
- Update as your business evolves
- Keep keywords current
- Refresh descriptions

### Test Settings
- Use SEO preview cards
- Test social sharing
- Verify in Google Search Console
- Monitor performance`
    },
    {
      id: 'addons',
      title: 'Package add-ons',
      icon: '➕',
      group: 'Services',
      content: `# Add-ons - Additional Services

Create and manage global add-ons that can be added to any package. Add-ons are additional services clients can purchase alongside their main package.

## Creating an Add-on

1. Click "Add-ons" in admin sidebar
2. Click "Add New Add-on" button
3. Fill in Add-on Details:
   - **Name**: Add-on name (e.g., "Extra Hour", "Second Photographer")
   - **Price**: Base price for the add-on
   - **Pricing Type**: 
     - **Fixed**: One-time price
     - **Per Hour**: Price multiplied by hours
     - **Per Each**: Price multiplied by quantity
   - **Currency**: R, USD, EUR, GBP, CAD, or AUD
   - **Description**: Optional description of the add-on

4. Click "Save Add-on"
5. Add-on is now available to add to any package

## Pricing Types Explained

### Fixed Pricing
- One-time price regardless of quantity
- Example: "Second Photographer" for R2,000 (fixed price)

### Per Hour Pricing
- Price multiplied by number of hours
- Example: "Extra Hour" at R500/hour × 3 hours = R1,500

### Per Each Pricing
- Price multiplied by quantity
- Example: "Additional Prints" at R50 each × 10 = R500

## Managing Add-ons

### Editing an Add-on
1. Click "Edit" button on any add-on
2. Modify details as needed
3. Click "Update Add-on" to save

### Deleting an Add-on
1. Click "Delete" button on any add-on
2. Confirm deletion
3. Add-on is removed from all packages

## Using Add-ons in Packages

When creating or editing a package:
1. Go to "Add-ons" section in package editor
2. Select from available global add-ons
3. Add-ons appear in package modal for clients to select
4. Prices are automatically calculated based on pricing type

## Best Practices

- **Clear Names**: Use descriptive names (e.g., "Extra Hour of Photography")
- **Reasonable Pricing**: Set prices that make sense for your market
- **Good Descriptions**: Help clients understand what they're getting
- **Consistent Currency**: Use your primary currency for most add-ons`
    },
    {
      id: 'currency-management',
      title: 'Currency Management',
      icon: '💱',
      group: 'Services',
      content: `# Currency Management - Multi-Currency Pricing

Configure automatic currency conversion with markup and rounding for international clients.

## How It Works

Your website automatically detects a visitor's location and shows prices in their local currency. Currency Management lets you:
- Enable/disable specific currencies
- Set markup percentages to cover conversion costs
- Configure price rounding for clean numbers
- Preview conversions in real-time

## Configuring a Currency

1. Click "Currency Management" in admin sidebar
2. Find the currency you want to configure (USD, EUR, GBP, CAD, AUD)
3. **Enable/Disable**: Toggle currency on or off
4. **Markup Percentage**: Set markup to cover fees (e.g., 5% for processing fees)
5. **Round To**: Set rounding (e.g., round to nearest $10)
6. Changes apply immediately

## Currency Settings Explained

### Markup Percentage
- Covers currency conversion fees
- Covers payment processing costs
- Ensures you don't lose money on conversions
- Example: 5% markup on $1,000 = $1,050

### Rounding
- Rounds prices to clean numbers
- Better customer experience
- Prevents odd prices like $1,234.56
- Example: Round to nearest $10: $1,234 → $1,240

## Automatic Detection

The system automatically:
- Detects visitor's location from IP address
- Detects browser language settings
- Shows prices in their local currency
- Converts using current exchange rates
- Applies your markup and rounding settings

## Preview Conversions

Use the preview section to see:
- How prices convert to each currency
- Final prices after markup and rounding
- Real-time exchange rates
- Impact of your settings

## Best Practices

- **Enable Major Currencies**: Enable USD, EUR, GBP for international reach
- **Reasonable Markup**: 3-7% is typical for conversion fees
- **Clean Rounding**: Round to nearest $10, $25, or $50
- **Test Conversions**: Preview before enabling to ensure prices make sense
- **Monitor Exchange Rates**: Adjust markup if exchange rates change significantly`
    },
    {
      id: 'quote-builder',
      title: 'Quote Builder',
      icon: '📋',
      group: 'Services',
      content: `# Quote Builder - Create Custom Quotes

Build professional, customized quotes for clients with packages, add-ons, discounts, and personal messages.

## Creating a Quote

1. Click "Quote Builder" in admin sidebar
2. **Step 1 - Select Package**: Choose the base package
3. **Step 2 - Select Add-ons**: Add any additional services
4. **Step 3 - Review & Customize**: 
   - Review total price
   - Add discount if needed
   - Override package price if custom
   - Add personal message
5. **Step 4 - Client Details**: Enter client name and email
6. Click "Generate Quote" to create

## Quote Features

### Package Selection
- Choose from all available packages
- See package details and pricing
- Filter by category if needed

### Add-ons Management
- Add multiple add-ons to quote
- Set quantities for per-each add-ons
- Set hours for per-hour add-ons
- Override add-on prices if needed

### Price Customization
- **Discount**: Apply percentage discount (0-100%)
- **Price Override**: Set custom package price
- **Add-on Overrides**: Customize individual add-on prices
- **Currency Selection**: Choose quote currency

### Personalization
- **Client Name**: Personalize the quote
- **Personal Message**: Add custom message
- **Contact Info**: Your business contact details

## Generating Quotes

### Preview Quote
1. Click "Generate Quote" after completing steps
2. Preview the formatted quote
3. Review all details
4. Make adjustments if needed

### Send Quote
1. Enter recipient email address
2. Click "Send Quote via Email"
3. Quote is sent as formatted HTML email
4. Client receives professional quote

### Download Quote
1. Open the client's quotation and click "Download PDF"
2. The file is a PDF you can print or send yourself
3. "Email PDF" sends that same file to the client's email address
4. On their quotation page, the client can also click "Download PDF"

## Quote Customization

### Discount Application
- Enter discount percentage (0-100)
- Applied to subtotal (package + add-ons)
- Final total shows discount amount
- Example: 10% off R10,000 = R9,000

### Price Overrides
- Override base package price for custom quotes
- Override individual add-on prices
- Useful for special deals or negotiations
- Original prices shown for reference

### Contact Information
- Your business name
- Email address
- Phone number
- Location
- Appears on all quotes

## Best Practices

- **Clear Pricing**: Show breakdown of package + add-ons
- **Professional Format**: Use generated HTML for consistency
- **Personal Touch**: Add client name and personal message
- **Timely Delivery**: Send quotes promptly after inquiry
- **Follow Up**: Track which quotes convert to bookings`
    },
    {
      id: 'theme',
      title: 'Theme Management',
      icon: '🎨',
      group: 'Brand & Customization',
      content: `# Theme Management - Customize Your Brand Colors

Customize your website's color scheme to match your brand identity. Change colors throughout the entire site with a few clicks.

## Accessing Theme Management

1. Click "Theme" in admin sidebar
2. See your current color palette
3. Choose from presets or customize manually

## Using Theme Presets

### Available Presets
- **Minimal Black**: Clean black and white design
- **Classic Warm**: Traditional browns and golds
- **Spring Bloom**: Fresh greens and pinks
- **Summer Sunshine**: Bright yellows and oranges
- **Ocean Breeze**: Cool blues and teals
- **Autumn Leaves**: Warm oranges and reds
- **Winter Elegance**: Sophisticated grays and silvers

### Applying a Preset
1. Scroll to "Theme Presets" section
2. Click on any preset card
3. Colors apply immediately
4. Preview changes in real-time

## Custom Color Palette

### Color Swatches
Your theme includes these color elements:
- **Primary**: Main brand color (buttons, links, accents)
- **Secondary**: Supporting color (headers, highlights)
- **Accent**: Accent color (special elements)
- **Background**: Page background color
- **Text**: Main text color
- **Surface**: Card/panel background
- **Border**: Border and divider color

### Customizing Colors
1. Click on a color swatch to select it
2. Use color picker to choose new color
3. Changes apply immediately
4. See live preview of your changes

## Live Preview

The preview section shows:
- **Buttons**: How primary, secondary, and accent colors look
- **Cards**: How surface and border colors work together
- **Text**: How text color appears on backgrounds
- **Real-time Updates**: See changes as you make them

## Saving Your Theme

### Preview Theme
1. Click "Preview Theme" button
2. Theme applies temporarily
3. Check your website to see changes
4. Make adjustments if needed

### Save Theme
1. Click "Save Theme" button
2. Theme is saved permanently
3. Applied across entire website
4. Persists across page reloads

### Reset Theme
1. Click "Reset to Default" button
2. Returns to default theme
3. All customizations removed
4. Can start fresh

## Best Practices

- **Brand Consistency**: Match your logo and brand colors
- **Readability**: Ensure text is readable on backgrounds
- **Contrast**: Use sufficient contrast for accessibility
- **Test Thoroughly**: Preview on different pages
- **Save Regularly**: Save when you're happy with changes

## Color Tips

- **Primary Color**: Use your main brand color
- **Secondary Color**: Use complementary or darker shade
- **Accent Color**: Use for call-to-action buttons
- **Background**: Keep light for readability
- **Text**: Dark on light backgrounds, light on dark
- **Surface**: Slightly different from background for depth`
    },
    {
      id: 'welcome-popup',
      title: 'Welcome popup',
      icon: '👋',
      group: 'Brand & Customization',
      content: `# Welcome Popup - First Impression

Manage the welcome popup that appears when visitors first arrive at your website. Customize quotes, enable/disable, and create a memorable first impression.

## What is the Welcome Popup?

The welcome popup is a modal that appears the first time a visitor opens the homepage. It features:
- Personalized greeting based on time of day
- Inspirational quote
- Your logo
- Professional, welcoming design

## Managing Quotes

### Adding a Quote
1. Click "Welcome popup" under Look
2. Click "Add New Quote" button
3. Fill in Quote Details:
   - **Quote Text**: The inspirational message
   - **Author**: Who said it (e.g., "Carmen Lorraine" or "Anonymous")
4. Click "Add Quote"
5. Quote is added to rotation

### Editing a Quote
1. Click "Edit" button on any quote
2. Modify quote text or author
3. Click "Update Quote"
4. Changes save immediately

### Deleting a Quote
1. Click "Delete" button on any quote
2. Confirm deletion
3. Quote is removed from rotation

## Quote Rotation

### How It Works
- Quotes rotate daily
- Same quote shown to all visitors on the same day
- Different quote each day
- Ensures variety and freshness

### Quote Selection
- System automatically selects quote based on date
- Ensures consistency (all visitors see same quote per day)
- Rotates through all available quotes
- Starts over when all quotes have been shown

## Enabling/Disabling Popup

### Enable Popup
1. Toggle "Enable Welcome Popup" switch ON
2. Popup appears for a first-time visitor
3. Shows once in that browser
4. Stays closed after they dismiss it

### Disable Popup
1. Toggle "Enable Welcome Popup" switch OFF
2. Popup no longer appears
3. Can re-enable anytime
4. Settings save automatically

## Personalized Greetings

The popup automatically shows greetings based on time:
- **Morning** (5am - 12pm): "Good Morning"
- **Afternoon** (12pm - 5pm): "Good Afternoon"
- **Evening** (5pm - 9pm): "Good Evening"
- **Night** (9pm - 5am): "Welcome"

## Best Practices

### Quote Selection
- **Inspirational**: Choose uplifting, positive quotes
- **Relevant**: Quotes related to photography or your brand
- **Variety**: Mix different types of quotes
- **Length**: Keep quotes concise (1-2 sentences)

### Timing
- **Once Per Day**: Prevents annoyance
- **First Visit**: Creates good first impression
- **Non-Intrusive**: Easy to close

### Content
- **Brand Voice**: Match your brand personality
- **Professional**: Keep it professional yet warm
- **Authentic**: Use quotes that resonate with you
- **Updated**: Refresh quotes periodically

## Tips

- **5-10 Quotes**: Maintain a good rotation
- **Regular Updates**: Add new quotes monthly
- **Test Popup**: Visit site as new visitor to see it
- **Logo Display**: Ensure your logo looks good in popup`
    },
    {
      id: 'settings',
      title: 'Share message',
      icon: '⚙️',
      group: 'System',
      content: `# Settings - System Configuration

Manage general settings and preferences for your website.

## Available Settings
- **Share Message Template**: Default message for social sharing when visitors share your content

## How to Configure Settings
1. Click "Settings" in admin sidebar
2. Enter your preferred share message template
3. Click "Save Settings"
4. Changes apply immediately

## Setting Features
- **Social Sharing**: Customize what message appears when content is shared
- **Real-time Updates**: Changes appear instantly
- **Simple Interface**: Easy to understand and modify`
    },
    {
      id: 'component-matrix',
      title: 'Component Matrix',
      icon: '🗂️',
      group: 'Reference',
      content: `# Component Matrix - Complete Reference

## 📊 Component Matrix

| Component | Admin Panel | Homepage | Modal | Purpose |
|-----------|-------------|----------|-------|---------|
| **Dashboard** | ✅ Overview | ❌ | ❌ | Statistics & quick actions |
| **AdminNavigation** | ✅ Sidebar | ❌ | ❌ | Navigation menu |
| **GalleryManagement** | ✅ Galleries | ❌ | ❌ | Gallery CRUD operations |
| **GalleryCreateModal** | ❌ | ❌ | ✅ Create | New gallery creation |
| **GalleryEditModal** | ❌ | ❌ | ✅ Edit | Edit gallery details |
| **CategoryManagement** | ✅ Categories | ❌ | ❌ | Gallery organization |
| **DisplaySettings** | ✅ Display | ❌ | ❌ | Homepage layout config |
| **OriginalGalleryView** | ❌ | ✅ Gallery | ❌ | Main gallery display |
| **MosaicGallery** | ❌ | ✅ Gallery | ❌ | Mosaic layout |
| **MagazineGallery** | ❌ | ✅ Gallery | ❌ | Magazine layout |
| **CarouselGallery** | ❌ | ✅ Gallery | ❌ | Carousel layout |
| **PortfolioGallery** | ❌ | ✅ Gallery | ❌ | Portfolio layout |
| **CinematicGallery** | ❌ | ✅ Gallery | ❌ | Cinematic layout |
| **BlogManagement** | ✅ Blog Posts | ❌ | ❌ | Blog post editor (one template, SEO fields included) |
| **BlogCategoryManagement** | ✅ Blog Categories | ❌ | ❌ | Category labels, intros, and SEO |
| **BlogSection** | ❌ | ✅ Blog | ❌ | Blog posts display |
| **LogoManagement** | ✅ Logo | ❌ | ❌ | Logo upload/management |
| **ContentManagement** | ✅ Website Content | ❌ | ❌ | Homepage text editing |
| **ServiceManagement** | ✅ Packages | ❌ | ❌ | Package CRUD operations |
| **PackagesGrid** | ❌ | ✅ Packages | ❌ | Packages display |
| **ServiceModal** | ❌ | ❌ | ✅ View | Package details |
| **Settings** | ✅ Settings | ❌ | ❌ | System configuration |
| **UserGuide** | ❌ | ❌ | ✅ Help | Interactive help system |
| **SEODashboard** | ✅ SEO | ❌ | ❌ | SEO management and optimization |
| **PageSEOEditor** | ✅ SEO Pages | ❌ | ❌ | Visual page SEO management |
| **SEOHealthChecker** | ✅ SEO Health | ❌ | ❌ | SEO audit and validation |
| **BulkSEOTools** | ✅ SEO Content | ❌ | ❌ | Bulk SEO operations |
| **SEOPreviewCard** | ✅ SEO | ❌ | ❌ | SEO preview (Google, Facebook, Twitter) |
| **GallerySEOEditor** | ✅ Gallery Edit | ❌ | ❌ | Individual gallery SEO |
| **ServiceSEOEditor** | ✅ Service Edit | ❌ | ❌ | Individual service SEO |

## 🎯 Component Categories

### **Admin Panel Components** (Management)
- Dashboard, AdminNavigation, GalleryManagement, CategoryManagement, DisplaySettings
- BlogManagement, BlogCategoryManagement, LogoManagement, ContentManagement
- ServiceManagement, Settings, SEODashboard, PageSEOEditor, SEOHealthChecker, BulkSEOTools

### **Homepage Components** (Display)
- OriginalGalleryView, MosaicGallery, MagazineGallery, CarouselGallery
- PortfolioGallery, CinematicGallery, BlogSection, PackagesGrid

### **Modal Components** (Interaction)
- GalleryCreateModal, GalleryEditModal, ServiceModal, UserGuide

## 🔄 Component Relationships

### **Gallery Flow**
1. **GalleryManagement** → **GalleryCreateModal** → **OriginalGalleryView**
2. **CategoryManagement** → **GalleryManagement** → **DisplaySettings** → **Homepage Views**

### **Blog Flow**
1. **BlogManagement** → public post template → **BlogSection**
2. **BlogCategoryManagement** → **BlogManagement** → **BlogSection**

### **Service Flow**
1. **ServiceManagement** → **PackagesGrid** → **ServiceModal**`
    },
    {
      id: 'technical-glossary',
      title: 'Technical Glossary',
      icon: '📖',
      group: 'Reference',
      content: `# Technical Terms Glossary

## 📊 Technical Terms Matrix

| Term | Category | Definition | Example/Context | Used In |
|------|----------|------------|-----------------|---------|
| **Slug** | Website | URL-friendly version of a title | "My Blog Post" → "my-blog-post" | Blog posts, galleries |
| **Modal** | Website | Popup window overlay | Image selection, form editing dialogs | Admin panels |
| **Responsive** | Website | Adapts to different screen sizes | Mobile, tablet, desktop layouts | All components |
| **Homepage** | Website | Main page visitors see first | Your website's landing page | Public site |
| **Admin Panel** | Website | Management area for your website | Where you add/edit content | Admin interface |
| **Sidebar** | Website | Left navigation menu | Admin panel navigation | Admin panels |
| **Dashboard** | Website | Overview page with statistics | Admin panel home page | Admin interface |
| **Preview** | Website | See changes before saving | Preview gallery before publishing | Admin panels |
| **Publish** | Website | Make content visible to visitors | Making a blog post live | Content management |
| **Draft** | Website | Unpublished content | Blog post being written | Content management |
| **Hero Image** | Photography | Main featured image | First image in a gallery or blog post | Galleries, blogs |
| **Mosaic Layout** | Photography | Varied-size image arrangement | Different sized gallery images | Gallery display |
| **Aspect Ratio** | Photography | Width to height proportion | 16:9 for landscape, 4:3 for portrait | Image display |
| **Alt Text** | Photography | Image description for accessibility | "Portrait of smiling couple" | All images |
| **Thumbnail** | Photography | Small preview image | Gallery image previews | Gallery grids |
| **Gallery** | Photography | Collection of related images | Wedding photos, portrait session | Content organization |
| **Category** | Photography | Image organization system | Portraits, Weddings, Commercial | Content organization |
| **Portfolio** | Photography | Showcase of best work | Featured galleries on homepage | Content display |
| **Client Gallery** | Photography | Private gallery for specific client | Password-protected gallery access | Client delivery |
| **Bulk Operations** | Admin | Actions on multiple items | Delete multiple galleries at once | Admin panels |
| **Drag & Drop** | Admin | Moving items by dragging | Reordering gallery images | Admin panels |
| **Multi-select** | Admin | Selecting multiple items | Choosing several images to delete | Admin panels |
| **Featured** | Admin | Highlighted content | Featured gallery on homepage | Content management |
| **SEO** | Admin | Search Engine Optimization | Making content discoverable | Content management |
| **Save** | Content | Store your changes | Clicking "Save" button to keep changes | All forms |
| **Auto-save** | Content | Automatic saving | Some changes save automatically | Forms |
| **Backup** | Content | Data copy for safety | Backup of galleries before changes | Data management |
| **Sync** | Content | Keep data updated | Changes appear across all devices | Data management |
| **Import** | Content | Bring in existing content | Uploading galleries from another system | Data management |
| **Export** | Content | Download your content | Getting a copy of your galleries | Data management |
| **Session** | Content | Your login time | How long you stay logged in | User management |
| **Login** | Content | Access your admin panel | Username and password entry | User management |

## 🎯 Term Categories

### **Website Terms** (General)
- Slug, Modal, Responsive, Homepage, Admin Panel, Sidebar, Dashboard, Preview, Publish, Draft

### **Photography Terms** (Industry-Specific)
- Hero Image, Mosaic Layout, Aspect Ratio, Alt Text, Thumbnail, Gallery, Category, Portfolio, Client Gallery

### **Admin Terms** (Management)
- Bulk Operations, Drag & Drop, Multi-select, Featured, SEO

### **Content Terms** (Data Management)
- Save, Auto-save, Backup, Sync, Import, Export, Session, Login

## 🔄 Term Relationships

### **Content Creation Flow**
1. **Draft** → **Preview** → **Publish** → **Homepage**
2. **Login** → **Admin Panel** → **Dashboard** → **Content Management**

### **Image Management Flow**
1. **Gallery** → **Category** → **Hero Image** → **Thumbnail** → **Alt Text**
2. **Drag & Drop** → **Multi-select** → **Bulk Operations** → **Save**

### **SEO & Discovery Flow**
1. **Slug** → **SEO** → **Responsive** → **Homepage** → **Portfolio**

### **SEO Management Flow**
1. **SEODashboard** → **PageSEOEditor** → **SEO Settings** → **SEO Preview**
2. **Content** → **Individual SEO Editors** → **SEO Preview** → **Save**
3. **Health Check** → **Detect Issues** → **One-Click Fixes** → **Monitor Progress**`
    }
  ];

  const currentSectionData = sections.find(section => section.id === activeSection) || sections[0];

  // Group sections by their group property
  const groupedSections = sections.reduce((groups, section) => {
    const group = section.group || 'Other';
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(section);
    return groups;
  }, {} as Record<string, typeof sections>);

  const renderMarkdown = (text: string) => {
    return text
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^#### (.*$)/gim, '<h4>$1</h4>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      .replace(/^\|(.+)\|$/gm, (match, content) => {
        const cells = content.split('|').map((cell: string) => cell.trim());
        return `<tr>${cells.map((cell: string) => `<td>${cell}</td>`).join('')}</tr>`;
      })
      .replace(/(<tr>.*<\/tr>)/s, (match) => {
        const rows = match.match(/<tr>.*?<\/tr>/g) || [];
        if (rows.length > 0) {
          const firstRow = rows[0];
          if (firstRow) {
            const isHeader = firstRow.includes('<td><strong>') || firstRow.includes('<td>**');
            if (isHeader) {
              return `<table><thead>${firstRow}</thead><tbody>${rows.slice(1).join('')}</tbody></table>`;
            }
          }
        }
        return `<table><tbody>${match}</tbody></table>`;
      })
      .replace(/\n/g, '<br>');
  };

  const modalContent = (
    <div className="user-guide-overlay" onClick={(e) => {
      // Close when clicking the overlay (but not the modal content)
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="user-guide-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-guide-header">
          <h2>User Guide</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="user-guide-content">
          <div className="user-guide-sidebar">
            <h3>Sections</h3>
            <div className="guide-sections">
              {Object.entries(groupedSections).map(([groupName, groupSections]) => (
                <div key={groupName} className="guide-group">
                  <div className="guide-group-title">{groupName}</div>
                  {groupSections.map(section => (
                    <button
                      key={section.id}
                      className={`guide-section-btn ${activeSection === section.id ? 'active' : ''}`}
                      onClick={() => setActiveSection(section.id)}
                    >
                      <span className="section-icon">{section.icon}</span>
                      <span className="section-title">{section.title}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          <div className="user-guide-main">
            <div className="guide-content">
              <div 
                className="markdown-content"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(currentSectionData.content) }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal at document body level using portal
  return typeof document !== 'undefined' 
    ? createPortal(modalContent, document.body)
    : modalContent;
};

export default UserGuide;